import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid2,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import useNotify from '../../hooks/useNotify';
import PageWithTitle from '../../components/atom/PageWithTitle';
import SearchInput from '../../components/atom/SearchInput';
import { useTranslation } from 'react-i18next';
import SvgEditor from '../../components/svgeditor/SvgEditor.jsx';
import SeatsTable from './components/SeatsTable.jsx';
import {
    useGetTicketSeats,
    getTicketSeats,
    addTicketSeat,
    editTicketSeat,
    deleteTicketSeat,
} from '@openapi/ticket-seats/ticket-seats';
import {
    useGetArrangements,
    createArrangement,
    deleteArrangement,
    useGetArrangementPositions,
    upsertArrangementPosition,
    useGetShow,
} from '@openapi/shows/shows';
import { useParams } from 'react-router-dom';
import mapFromDbToElement from './helpers/mapFromDbToElement.jsx';
import mapElementRowToDb from './helpers/mapElementRowToDb.jsx';
import { collectAllData, exportToXlsx, customMapping } from '../../helpers/excelExportHelpers.js';
import { applyArrangementOverlay, buildPositionMap } from '../../helpers/arrangementOverlay';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../hooks/useAuth';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';

const SeatsPage = () => {
    const { showId } = useParams();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { notifyError, notifySuccess } = useNotify();
    const [search, setSearch] = useState('');
    const [elements, setElements] = useState([]);
    const [selectedElementId, setSelectedElementId] = useState(null);
    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);
    const [selectedArrangementId, setSelectedArrangementId] = useState(null);
    const [localPositionMap, setLocalPositionMap] = useState({});
    const [addingArrangement, setAddingArrangement] = useState(false);
    const newArrangementNameRef = useRef(null);
    const { user } = useAuth();
    const { showModal } = useModals();

    const {
        data,
        isLoading: loading,
        refetch,
    } = useGetTicketSeats(
        Number(showId),
        {
            pageSize: 10000,
            page: 0,
            search,
        },
        {
            query: {
                enabled: !!showId,
                onError: notifyError,
            },
        },
    );

    const { data: show, isLoading: loadingShow } = useGetShow(Number(showId), {
        query: {
            enabled: !!showId,
            onError: notifyError,
        },
    });

    const { data: arrangementsData, refetch: refetchArrangements } = useGetArrangements(
        Number(showId),
        {
            query: {
                enabled: !!showId,
                onError: notifyError,
            },
        },
    );

    const { data: positionsData } = useGetArrangementPositions(
        Number(showId),
        selectedArrangementId,
        {
            query: {
                enabled: !!showId && !!selectedArrangementId,
                onError: notifyError,
            },
        },
    );

    useEffect(() => {
        if (!data) return;
        setElements(data?.ticketSeats.map(mapFromDbToElement));
    }, [data, loading]);

    // Sync server positions into local state (reset when arrangement changes or data loads)
    useEffect(() => {
        if (!positionsData?.positions) {
            setLocalPositionMap({});
            return;
        }
        setLocalPositionMap(buildPositionMap(positionsData.positions));
    }, [positionsData]);

    // Reset local positions when switching arrangements
    useEffect(() => {
        setLocalPositionMap({});
    }, [selectedArrangementId]);

    // Elements with arrangement overlay applied (uses local optimistic positions)
    const arrangementElements = useMemo(() => {
        if (!selectedArrangementId) return elements;
        return applyArrangementOverlay(elements, localPositionMap).displayElements;
    }, [elements, selectedArrangementId, localPositionMap]);

    const unplacedElements = useMemo(() => {
        if (!selectedArrangementId) return [];
        return elements.filter((el) => localPositionMap[el.id]?.removed === true);
    }, [elements, selectedArrangementId, localPositionMap]);

    const deleteSeat = useCallback(
        async (id) => {
            try {
                await deleteTicketSeat(Number(showId), id);
                notifySuccess(t('seats.notifications.deleted'));
                setElements((prev) => prev.filter((el) => el.id !== id));
            } catch (_err) {
                notifyError(t('seats.notifications.deleteError'));
            }
        },
        [notifyError, notifySuccess, t, showId],
    );

    const addElement = async (elementTemplate) => {
        const dbTicket = mapElementRowToDb(elementTemplate);
        try {
            const res = await addTicketSeat(parseInt(showId, 10), {
                name: dbTicket.name,
                nameI18n: dbTicket.nameI18n,
                svgInfo: dbTicket.svgInfo,
            });
            notifySuccess(t('seats.notifications.added'));
            setElements((prev) => [
                ...prev,
                {
                    ...elementTemplate,
                    id: res.id,
                },
            ]);
        } catch (_err) {
            notifyError(t('seats.notifications.createError'));
        }
    };

    const modifyElement = useCallback(
        (mapFunction, id) => {
            setElements((prev) =>
                prev.map((item) => {
                    const result = item.id === id ? mapFunction(item) : item;
                    result.x = Math.round(result.x);
                    result.y = Math.round(result.y);
                    result.rotation = Math.round(result.rotation);

                    if (item.id === id) {
                        const dbTicket = mapElementRowToDb(result);
                        editTicketSeat(parseInt(showId, 10), dbTicket.id, {
                            name: dbTicket.name,
                            nameI18n: dbTicket.nameI18n,
                            svgInfo: dbTicket.svgInfo,
                            bookable: dbTicket.bookable,
                        }).catch(() => {
                            notifyError(t('seats.notifications.updateError'));
                        });
                    }
                    return result;
                }),
            );
        },
        [notifyError, showId, t, setElements],
    );

    const modifyArrangementElement = useCallback(
        (mapFunction, id) => {
            const item = elements.find((el) => el.id === id);
            if (!item) return;
            const currentPos = localPositionMap[id] || {
                x: item.x,
                y: item.y,
                rotation: item.rotation,
                scale: item.scale,
                model: item.model,
                svg: item.svg,
            };
            const result = mapFunction({ ...item, ...currentPos });
            const newSvgInfo = {
                ...currentPos,
                x: Math.round(result.x),
                y: Math.round(result.y),
                rotation: Math.round(result.rotation),
                scale: result.scale,
            };
            setLocalPositionMap((prev) => ({ ...prev, [id]: newSvgInfo }));
            upsertArrangementPosition(Number(showId), selectedArrangementId, id, {
                svgInfo: newSvgInfo,
            }).catch(() => {
                notifyError(t('seats.notifications.updateError'));
            });
        },
        [elements, localPositionMap, showId, selectedArrangementId, notifyError, t],
    );

    const addToArrangement = useCallback(
        (element) => {
            setLocalPositionMap((prev) => {
                const next = { ...prev };
                delete next[element.id];
                return next;
            });
            upsertArrangementPosition(Number(showId), selectedArrangementId, element.id, {
                svgInfo: null,
            }).catch(() => {
                notifyError(t('seats.notifications.updateError'));
            });
        },
        [showId, selectedArrangementId, notifyError, t],
    );

    const removeFromArrangement = useCallback(
        (id) => {
            setLocalPositionMap((prev) => ({ ...prev, [id]: { removed: true } }));
            upsertArrangementPosition(Number(showId), selectedArrangementId, id, {
                svgInfo: { removed: true },
            }).catch(() => {
                notifyError(t('seats.notifications.updateError'));
            });
        },
        [showId, selectedArrangementId, notifyError, t],
    );

    const updateNameI18n = (id, nameI18n) => {
        setElements((prev) =>
            prev.map((item) => {
                const result = item.id === id ? { ...item, nameI18n } : item;
                if (item.id === id) {
                    const dbTicket = mapElementRowToDb(result);
                    editTicketSeat(parseInt(showId, 10), dbTicket.id, {
                        name: dbTicket.name,
                        nameI18n: dbTicket.nameI18n,
                        svgInfo: dbTicket.svgInfo,
                        bookable: dbTicket.bookable,
                    }).catch(() => {
                        notifyError(t('seats.notifications.updateError'));
                    });
                }
                return result;
            }),
        );
    };

    const handleAddArrangement = async () => {
        const name = newArrangementNameRef.current?.value ?? '';
        if (!name.trim()) return;
        try {
            await createArrangement(Number(showId), { name: name.trim() });
            setAddingArrangement(false);
            refetchArrangements();
            notifySuccess(t('seats.notifications.arrangementAdded'));
        } catch (_err) {
            notifyError(t('seats.notifications.arrangementError'));
        }
    };

    const handleDeleteArrangement = useCallback(
        (id) => {
            showModal(MODAL_TYPE.CONFIRMATION, {
                title: t('confirmationModal.title'),
                content: t('seats.deleteArrangementConfirm'),
                confirm: async () => {
                    try {
                        await deleteArrangement(Number(showId), id);
                        if (selectedArrangementId === id) setSelectedArrangementId(null);
                        refetchArrangements();
                        notifySuccess(t('seats.notifications.arrangementDeleted'));
                    } catch (_err) {
                        notifyError(t('seats.notifications.arrangementError'));
                    }
                },
            });
        },
        [
            showId,
            selectedArrangementId,
            refetchArrangements,
            notifySuccess,
            notifyError,
            showModal,
            t,
        ],
    );

    const arrangements = arrangementsData?.arrangements || [];

    const isArrangementMode = !!selectedArrangementId;
    const displayElements = isArrangementMode ? arrangementElements : elements;

    if (loading || loadingShow) return null;

    return (
        <PageWithTitle
            title={t('seats.title')}
            inlineHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    wrap="nowrap"
                    key="search-and-buttons"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 2 }}>
                            <SearchInput search={search} setSearch={setSearch} />
                        </Grid2>
                    )}
                    {!isArrangementMode && user.isSuperAdmin && elements.length > 0 && (
                        <Grid2 sx={{ flexShrink: 0 }}>
                            <Tooltip title={t('seats.cloneTitle')}>
                                <Button
                                    fullWidth
                                    onClick={() =>
                                        showModal(MODAL_TYPE.SEATING_CLONE, {
                                            showId,
                                            elements,
                                        })
                                    }
                                >
                                    <ContentCopyIcon />
                                </Button>
                            </Tooltip>
                        </Grid2>
                    )}
                    {!isArrangementMode && (
                        <Grid2 sx={{ flexShrink: 0 }}>
                            <Tooltip title={t('seats.importTitle')}>
                                <Button
                                    fullWidth
                                    onClick={() =>
                                        showModal(MODAL_TYPE.SEATING_IMPORT, {
                                            showId,
                                            elements,
                                            onSuccess: refetch,
                                        })
                                    }
                                >
                                    <FileUploadIcon />
                                </Button>
                            </Tooltip>
                        </Grid2>
                    )}
                    <Grid2 sx={{ flexShrink: 0 }}>
                        <Tooltip title="Export">
                            <Button
                                fullWidth
                                disabled={generatingXlsx}
                                onClick={async () => {
                                    setGeneratingXlsx(true);
                                    let listToExport = [];
                                    try {
                                        if (isArrangementMode) {
                                            listToExport = customMapping(
                                                displayElements.map((el) => ({
                                                    id: el.id,
                                                    bookable: el.bookable,
                                                    name: el.name,
                                                    nameI18n: el.nameI18n,
                                                    svgInfo: {
                                                        x: el.x,
                                                        y: el.y,
                                                        rotation: el.rotation,
                                                        scale: el.scale,
                                                        model: el.model,
                                                    },
                                                })),
                                                'seats',
                                            );
                                        } else {
                                            listToExport = await collectAllData({
                                                data,
                                                apiFunction: (page, pageSize) =>
                                                    getTicketSeats(Number(showId), {
                                                        page,
                                                        pageSize,
                                                        search,
                                                    }),
                                                setProgress,
                                                responseData: 'ticketSeats',
                                                mappingName: 'seats',
                                            });
                                        }
                                        exportToXlsx(listToExport, 'seats');
                                        notifySuccess(t('seats.notifications.exportSuccess'));
                                    } catch (err) {
                                        notifyError(err);
                                    }
                                    setGeneratingXlsx(false);
                                    setProgress(0);
                                }}
                                label={t('common.export')}
                            >
                                {generatingXlsx ? (
                                    <CircularProgress
                                        variant="determinate"
                                        color="inherit"
                                        size={25}
                                        value={progress}
                                    />
                                ) : (
                                    <FileDownloadIcon />
                                )}
                            </Button>
                        </Tooltip>
                    </Grid2>
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }}>
                    <SearchInput search={search} setSearch={setSearch} fullWidth />
                </Grid2>
            )}
            <Grid2 size={{ xs: 12 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        alignItems: 'flex-start',
                        mb: 2,
                    }}
                >
                    {/* Left sidebar: arrangement list - same height as map */}
                    <Box
                        sx={{
                            width: { xs: '100%', md: 200 },
                            flexShrink: 0,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography variant="caption" sx={{ px: 1, color: 'text.secondary' }}>
                            {t('seats.arrangements')}
                        </Typography>
                        <List dense disablePadding>
                            <ListItemButton
                                selected={!selectedArrangementId}
                                onClick={() => setSelectedArrangementId(null)}
                            >
                                <ListItemText primary={t('seats.defaultArrangement')} />
                            </ListItemButton>
                            {arrangements.map((arr) => (
                                <ListItemButton
                                    key={arr.id}
                                    selected={selectedArrangementId === arr.id}
                                    onClick={() => setSelectedArrangementId(arr.id)}
                                    sx={{ pr: 0.5 }}
                                >
                                    <ListItemText
                                        primary={
                                            arr.id === show?.activeArrangementId
                                                ? `${arr.name} (${t('seats.active')})`
                                                : arr.name
                                        }
                                        primaryTypographyProps={{ noWrap: true }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteArrangement(arr.id);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItemButton>
                            ))}
                        </List>
                        <Divider sx={{ my: 1 }} />
                        {addingArrangement ? (
                            <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                    size="small"
                                    autoFocus
                                    inputRef={newArrangementNameRef}
                                    placeholder={t('seats.arrangementName')}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddArrangement();
                                        if (e.key === 'Escape') setAddingArrangement(false);
                                    }}
                                    fullWidth
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleAddArrangement}
                                    >
                                        {t('common.save')}
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setAddingArrangement(false)}
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                fullWidth
                                onClick={() => setAddingArrangement(true)}
                            >
                                {t('seats.addArrangement')}
                            </Button>
                        )}
                    </Box>

                    {/* Map */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SvgEditor
                            elements={displayElements}
                            addElement={isArrangementMode ? addToArrangement : addElement}
                            modifyElement={
                                isArrangementMode ? modifyArrangementElement : modifyElement
                            }
                            selectedElementId={selectedElementId}
                            setSelectedElementId={setSelectedElementId}
                            deleteSeat={isArrangementMode ? removeFromArrangement : deleteSeat}
                            mode={isArrangementMode ? 'arrangement' : 'regular'}
                            unplacedElements={unplacedElements}
                        />
                    </Box>
                </Box>

                {/* Table - full width below sidebar+map row */}
                <SeatsTable
                    rows={elements}
                    loading={false}
                    editedId={selectedElementId}
                    setEdited={setSelectedElementId}
                    deleteSeat={deleteSeat}
                    updateNameI18n={updateNameI18n}
                    venueId={show?.venueId}
                />
            </Grid2>
        </PageWithTitle>
    );
};

export default SeatsPage;
